import { useEffect, useState } from 'react';
import { api } from './api';
import { hostingBlocked, hostingError } from './hostingPresentation';
import type { MessageKey } from './i18n';
import type { HostingStatus, Room } from './types';

export function RoomHostingPanel({ room, t }: { room?: Room; t: (key: MessageKey) => string }) {
  const [status, setStatus] = useState<HostingStatus>();
  const [confirmation, setConfirmation] = useState<{ from: string; name: string }>();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MessageKey>();
  useEffect(() => {
    let disposed = false;
    let timer: number | undefined;
    const poll = () => void api.hosting().then(value => {
      if (!disposed) setStatus(value);
    }).catch(() => { if (!disposed) setStatus(undefined); }).finally(() => {
      if (!disposed) timer = window.setTimeout(poll, 2000);
    });
    poll();
    return () => { disposed = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, []);

  const blocked = hostingBlocked(status, room);
  const hosted = Boolean(room && status?.room_id === room.id && !status.pending_room_id);
  const activate = async () => {
    if (!room || !confirmation || busy) return;
    setBusy(true); setResult(undefined);
    try {
      const response = await api.hostRoom(room.id, confirmation.from);
      setResult(response.ok && response.room_id === room.id ? 'hostingComplete' : 'hostingUnconfirmed');
    } catch (error) {
      setResult(hostingError(error instanceof Error ? error.message : ''));
    } finally {
      setConfirmation(undefined); setBusy(false);
      void api.hosting().then(setStatus).catch(() => setStatus(undefined));
    }
  };
  return <section className="editor-panel hosting-panel">
    <h2>{t('hostingTitle')}</h2>
    <p className="field-help">{t('hostingNotice')}</p>
    <dl><dt>{t('hostingCurrent')}</dt><dd>{status?.room_name ?? t('hostingUnavailable')}</dd>
      <dt>{t('hostingSelected')}</dt><dd>{room?.name ?? t('noRooms')}</dd></dl>
    {hosted ? <p className="form-success">{t('hostingAlreadyActive')}</p> :
      <button className="primary-button" disabled={busy || Boolean(blocked) || Boolean(confirmation)}
        onClick={() => { if (status) { setConfirmation({ from: status.room_id, name: status.room_name }); setResult(undefined); } }}>
        {t(busy ? 'hostingBusy' : 'hostingActivate')}
      </button>}
    {!hosted && blocked && <p className="field-help" role="status">{t(blocked)}</p>}
    {confirmation && <div className="hosting-confirmation">
      <p>{t('hostingConfirm')}</p><p><strong>{confirmation.name}</strong> → <strong>{room?.name}</strong></p>
      <p className="field-help">{t('hostingConfirmNotice')}</p>
      <div className="hosting-actions"><button className="danger-button" disabled={busy || Boolean(blocked)} onClick={() => void activate()}>{t(busy ? 'hostingBusy' : 'hostingConfirmAction')}</button>
        <button className="secondary-button" disabled={busy} onClick={() => setConfirmation(undefined)}>{t('cancel')}</button></div>
    </div>}
    {result && <p role="status" className={result === 'hostingComplete' ? 'form-success' : 'form-error'}>{t(result)}</p>}
    {!result && status?.error_code && <p className="form-error" role="status">{t(hostingError(status.error_code))}</p>}
  </section>;
}
